import { useEffect, useMemo, useRef, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useRefetchWhenCatalogChanges } from '../contexts/AdminCatalogContext';
import { useAdminDialog } from '../contexts/AdminDialogContext';
import {
  addCidade,
  addOrUpdateRegiao,
  getCidades,
  getCidadesByRegioes,
  getRegioesByEstado,
  getRegioesSugeridasConhecidas,
  removeRegiao,
  removeCidade,
  type Cidade,
  type TransportOpcoes,
  updateCidade,
} from '../lib/experienciasStorage';
import { pushAdminSyncToApiDeferred } from '../lib/adminSyncApi';
import { compressImageFileForStorage } from '../lib/imageProcessing';
import { getApiBase } from '../lib/api';
import { uploadAdminMedia } from '../lib/mediaUploadApi';
import { migrateAllCidadeFotosToCloud } from '../lib/migrateExperienciaImages';
import { resolveMediaUrl } from '../../../shared/lib/mediaUrl';

type ParsedCoordinates = { lat: number; lng: number };

const PAISES = ['Brasil'];
const ESTADOS = ['Goiás', 'Distrito Federal'];

/** Translado padrão ao cadastrar uma nova cidade (Van 16 / Van 22 ficam desmarcados). */
function defaultTransportOpcoesNovaCidade(): TransportOpcoes {
  return { passeio: true, suv4x4: true, suv7lugares: true, taxi: true };
}

function isValidCoordinates(lat?: number, lng?: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lng) && (lat as number) >= -90 && (lat as number) <= 90 && (lng as number) >= -180 && (lng as number) <= 180;
}

function normalizePais(longName?: string, shortName?: string): string {
  const long = (longName ?? '').trim().toLowerCase();
  const short = (shortName ?? '').trim().toUpperCase();
  if (short === 'BR') return 'Brasil';
  if (long === 'brazil' || long === 'brasil') return 'Brasil';
  return longName ?? 'Brasil';
}

function normalizeEstado(longName?: string, shortName?: string): string {
  const long = (longName ?? '').trim().toLowerCase();
  const short = (shortName ?? '').trim().toUpperCase();
  if (short === 'GO' || long === 'goias' || long === 'goiás' || long === 'state of goias' || long === 'estado de goias' || long === 'estado de goiás') return 'Goiás';
  if (short === 'DF' || long === 'distrito federal') return 'Distrito Federal';
  return longName ?? '';
}

export default function CidadesPage() {
  const { showConfirm, showAlert } = useAdminDialog();
  const [listCidades, setListCidades] = useState<Cidade[]>([]);
  const [modalError, setModalError] = useState('');
  const [novaRegiaoInput, setNovaRegiaoInput] = useState('');
  /** true = formulário visível (adicionar/editar), false = página principal com lista */
  const [isFormView, setIsFormView] = useState(false);
  const [regioesVersion, setRegioesVersion] = useState(0);
  const [mapsReady, setMapsReady] = useState(false);
  const [mapsError, setMapsError] = useState<string | null>(null);
  const [savingCidade, setSavingCidade] = useState(false);
  const [fotoAddBusy, setFotoAddBusy] = useState(false);
  const [migracaoFotosBusy, setMigracaoFotosBusy] = useState(false);
  const [modalCidadeCoords, setModalCidadeCoords] = useState<ParsedCoordinates | null>(null);
  const [modalCidade, setModalCidade] = useState<{ id: string | null; pais: string; estadoProvincia: string; regioes: string[]; cep: string; cidade: string; lat?: number; lng?: number; fotos?: string[]; fotoPrincipalIndex?: number; transportOpcoes?: TransportOpcoes }>({
    id: null,
    pais: 'Brasil',
    estadoProvincia: '',
    regioes: [],
    cep: '',
    cidade: '',
    lat: undefined,
    lng: undefined,
    fotos: [],
    fotoPrincipalIndex: 0,
    transportOpcoes: defaultTransportOpcoesNovaCidade(),
  });

  const googleMapsApiKey = ((import.meta as { env?: Record<string, string | undefined> }).env?.VITE_GOOGLE_MAPS_API_KEY ?? '').trim();
  const cidadeInputRef = useRef<HTMLInputElement | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<{ setCenter: (coords: ParsedCoordinates) => void; setZoom: (zoom: number) => void } | null>(null);
  const markerRef = useRef<{ setPosition: (coords: ParsedCoordinates) => void } | null>(null);
  const autocompleteRef = useRef<{ addListener: (event: string, cb: () => void) => void; getPlace: () => { geometry?: { location?: { lat: () => number; lng: () => number } }; address_components?: Array<{ long_name: string; short_name: string; types: string[] }> } } | null>(null);

  const sortByMostRecent = (items: Cidade[]): Cidade[] => {
    const getTime = (id: string): number => {
      const m = /^cid-(\d+)-/.exec(id);
      return m ? Number(m[1]) : 0;
    };
    return [...items].sort((a, b) => getTime(b.id) - getTime(a.id));
  };

  const refreshCidades = () => {
    setListCidades(sortByMostRecent(getCidades()));
  };

  useRefetchWhenCatalogChanges(() => refreshCidades());

  const handleMigrarFotosCidadesParaNuvem = async () => {
    if (migracaoFotosBusy) return;
    const ok =
      typeof window !== 'undefined' &&
      window.confirm(
        'Enviar fotos de todas as cidades para o armazenamento (S3) via API? Use VITE_ADMIN_SYNC_URL no .env.local para apontar à produção. Pode demorar.'
      );
    if (!ok) return;
    setMigracaoFotosBusy(true);
    try {
      const r = await migrateAllCidadeFotosToCloud();
      refreshCidades();
      await showAlert(
        `Concluído: ${r.cidadesAtualizadas} cidade(s) atualizada(s), ${r.fotosMigradas} foto(s) migrada(s).` +
          (r.erros > 0 ? ` ${r.erros} falha(s); essas URLs foram mantidas.` : '') +
          ' Depois use no Dashboard «Enviar catálogo agora» para gravar no Dynamo de produção.',
        'Migração'
      );
    } catch (e) {
      await showAlert(e instanceof Error ? e.message : 'Erro na migração.', 'Erro');
    } finally {
      setMigracaoFotosBusy(false);
    }
  };

  useEffect(() => {
    refreshCidades();
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const regioesDisponiveis = useMemo(() => {
    void regioesVersion;
    return modalCidade.estadoProvincia.trim() ? getRegioesByEstado(modalCidade.estadoProvincia) : [];
  }, [modalCidade.estadoProvincia, regioesVersion]);

  const clearForm = () => {
    mapRef.current = null;
    markerRef.current = null;
    autocompleteRef.current = null;
    setModalCidade({ id: null, pais: 'Brasil', estadoProvincia: '', regioes: [], cep: '', cidade: '', lat: undefined, lng: undefined, fotos: [], fotoPrincipalIndex: 0, transportOpcoes: defaultTransportOpcoesNovaCidade() });
    setModalCidadeCoords(null);
    setModalError('');
  };

  const goToMainPage = () => {
    clearForm();
    setIsFormView(false);
  };

  const openAddCidadeForm = () => {
    clearForm();
    setIsFormView(true);
  };

  const editCidade = (c: Cidade) => {
    mapRef.current = null;
    markerRef.current = null;
    autocompleteRef.current = null;
    const regioesCadastradas = c.regioes ?? [];
    const regioesSugeridas = regioesCadastradas.length === 0
      ? getRegioesSugeridasConhecidas(c.cidade, c.estadoProvincia)
      : [];
    const regioes = regioesCadastradas.length > 0 ? regioesCadastradas : regioesSugeridas;
    regioesSugeridas.forEach((r) => addOrUpdateRegiao(r, c.estadoProvincia));
    if (regioesSugeridas.length > 0) setRegioesVersion((v) => v + 1);
    setModalCidade({
      id: c.id,
      cidade: c.cidade,
      pais: c.pais,
      estadoProvincia: c.estadoProvincia,
      regioes: [...regioes],
      cep: c.cep,
      lat: c.lat,
      lng: c.lng,
      fotos: [...(c.fotos ?? [])],
      fotoPrincipalIndex: c.fotoPrincipalIndex ?? 0,
      transportOpcoes: c.transportOpcoes ? { ...c.transportOpcoes } : undefined,
    });
    setModalCidadeCoords(isValidCoordinates(c.lat, c.lng) ? { lat: Number(c.lat), lng: Number(c.lng) } : null);
    setIsFormView(true);
  };

  const deleteCidade = async (c: Cidade) => {
    const confirmed = await showConfirm(`Excluir "${c.cidade}"?`, {
      title: 'Confirmar exclusão',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
    });
    if (!confirmed) return;
    const result = removeCidade(c.id);
    if (!result.ok) {
      setModalError(result.error ?? 'Erro ao excluir.');
      return;
    }
    refreshCidades();
    if (modalCidade.id === c.id) goToMainPage();
    pushAdminSyncToApiDeferred(async (syncResult) => {
      if (!syncResult.ok) {
        await showAlert(
          `Cidade excluída localmente. A sincronização com a API (AWS) falhou: ${syncResult.error ?? 'erro desconhecido'}. Use "Sincronizar com API" no Dashboard para tentar de novo.`,
          'Atenção'
        );
      }
    });
  };

  const saveCidade = async () => {
    const c = modalCidade.cidade.trim();
    const p = modalCidade.pais.trim();
    const estado = modalCidade.estadoProvincia.trim();
    const cep = modalCidade.cep.trim();

    const fail = async (msg: string) => {
      setModalError(msg);
      await showAlert(msg, 'Não foi possível salvar');
    };

    if (!c) return void (await fail('Cidade/distrito é obrigatório.'));
    if (!p) {
      return void (await fail('País deve ser carregado automaticamente. Digite o nome da cidade e escolha uma sugestão da lista (Google Places).'));
    }
    if (!estado) {
      return void (await fail('Estado/Província deve ser carregado automaticamente. Escolha a cidade na lista de sugestões ao digitar.'));
    }
    if (!cep) {
      return void (await fail('CEP deve ser carregado automaticamente. Escolha a cidade na lista de sugestões ao digitar.'));
    }
    const fotos = modalCidade.fotos ?? [];
    if (!modalCidade.id && fotos.length < 1) {
      return void (await fail('É obrigatório pelo menos uma foto: use "Adicionar" para enviar uma imagem.'));
    }

    setModalError('');
    const coords = modalCidadeCoords && isValidCoordinates(modalCidadeCoords.lat, modalCidadeCoords.lng)
      ? modalCidadeCoords
      : (isValidCoordinates(modalCidade.lat, modalCidade.lng) ? { lat: Number(modalCidade.lat), lng: Number(modalCidade.lng) } : null);

    const fotoPrincipalIndex = Math.min(modalCidade.fotoPrincipalIndex ?? 0, Math.max(0, fotos.length - 1));
    setSavingCidade(true);
    try {
      if (modalCidade.id) {
        updateCidade(modalCidade.id, {
          cidade: c,
          pais: p,
          estadoProvincia: estado,
          regioes: modalCidade.regioes,
          cep,
          lat: coords?.lat,
          lng: coords?.lng,
          fotos: fotos.length > 0 ? fotos : undefined,
          fotoPrincipalIndex: fotos.length > 0 ? fotoPrincipalIndex : undefined,
          transportOpcoes: modalCidade.transportOpcoes,
        });
      } else {
        addCidade({
          cidade: c,
          pais: p,
          estadoProvincia: estado,
          regioes: modalCidade.regioes,
          cep,
          lat: coords?.lat,
          lng: coords?.lng,
          fotos,
          fotoPrincipalIndex,
          transportOpcoes: modalCidade.transportOpcoes,
        });
      }
      refreshCidades();
      goToMainPage();
      pushAdminSyncToApiDeferred(async (syncResult) => {
        if (!syncResult.ok) {
          await showAlert(
            `${modalCidade.id ? 'Cidade atualizada' : 'Cidade cadastrada'} localmente, mas a sincronização com a API falhou: ${syncResult.error ?? 'erro desconhecido'}. Use "Sincronizar com API" no Dashboard para tentar de novo.`,
            'Atenção'
          );
        }
      });
    } catch (err) {
      await showAlert(err instanceof Error ? err.message : 'Erro ao salvar no armazenamento local.', 'Erro ao salvar');
    } finally {
      setSavingCidade(false);
    }
  };

  const deleteRegiao = async (regiao: string) => {
    const cidadesComRegiao = getCidadesByRegioes([regiao]);
    if (cidadesComRegiao.length > 0) {
      const listaCidades = cidadesComRegiao.map((c) => `• ${c.cidade}${c.estadoProvincia ? ` (${c.estadoProvincia})` : ''}`).join('\n');
      await showAlert(
        `Não é possível excluir a região "${regiao}" pois há ${cidadesComRegiao.length} cidade(s) associada(s):\n\n${listaCidades}\n\nRemova a região das cidades antes de excluí-la.`,
        'Região em uso'
      );
      return;
    }
    const confirmed = await showConfirm(`Excluir a região "${regiao}"?`, {
      title: 'Confirmar exclusão',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
    });
    if (!confirmed) return;
    removeRegiao(regiao);
    setModalCidade((m) => ({ ...m, regioes: m.regioes.filter((r) => r !== regiao) }));
    setRegioesVersion((v) => v + 1);
    refreshCidades();
    await showAlert(`Região "${regiao}" excluída com sucesso.`, 'Aviso');
  };

  const applyPlaceToModal = (place: {
    geometry?: { location?: { lat: () => number; lng: () => number } };
    address_components?: Array<{ long_name: string; short_name: string; types: string[] }>;
  }) => {
    const lat = place.geometry?.location?.lat?.();
    const lng = place.geometry?.location?.lng?.();
    const components = place.address_components ?? [];
    const byType = (type: string) => components.find((comp) => comp.types.includes(type));
    const cidadeComponente = byType('locality') ?? byType('administrative_area_level_2') ?? byType('sublocality') ?? byType('route');
    const estadoComponente = byType('administrative_area_level_1');
    const paisComponente = byType('country');
    const cepComponente = byType('postal_code');
    const estadoNormalizado = normalizeEstado(estadoComponente?.long_name, estadoComponente?.short_name);
    const paisNormalizado = normalizePais(paisComponente?.long_name, paisComponente?.short_name);

    const cidade = cidadeComponente?.long_name ?? '';
    const estado = estadoNormalizado ?? '';
    const regioesSugeridas = getRegioesSugeridasConhecidas(cidade, estado);
    regioesSugeridas.forEach((r) => addOrUpdateRegiao(r, estado));
    setModalCidade((m) => {
      const regioes = m.regioes.length > 0 ? m.regioes : regioesSugeridas;
      return {
        ...m,
        cidade: cidade || m.cidade,
        estadoProvincia: estado || m.estadoProvincia,
        pais: paisNormalizado || m.pais,
        cep: cepComponente?.long_name || m.cep,
        lat: Number.isFinite(lat) ? Number(lat) : m.lat,
        lng: Number.isFinite(lng) ? Number(lng) : m.lng,
        regioes,
      };
    });

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const coords = { lat: Number(lat), lng: Number(lng) };
    setModalCidadeCoords(coords);

    const googleMaps = (window as { google?: { maps?: { Geocoder: new () => { geocode: (req: Record<string, unknown>, cb: (results: Array<{ address_components?: Array<{ long_name: string; short_name: string; types: string[] }>; place_id?: string }> | null, status: string) => void) => void } } } }).google?.maps;
    if (!googleMaps?.Geocoder) return;
    const geocoder = new googleMaps.Geocoder();
    geocoder.geocode({ location: coords }, (results, status) => {
      if (status !== 'OK' || !results?.[0]) return;
      const first = results[0];
      const componentsGeo = first.address_components;
      const byTypeGeo = (type: string) => componentsGeo?.find((c) => c.types.includes(type));
      const estadoGeo = byTypeGeo('administrative_area_level_1');
      const paisGeo = byTypeGeo('country');
      const cepGeo = byTypeGeo('postal_code');
      const estadoGeoNorm = normalizeEstado(estadoGeo?.long_name, estadoGeo?.short_name);
      const paisGeoNorm = normalizePais(paisGeo?.long_name, paisGeo?.short_name);
      setModalCidade((m) => ({
        ...m,
        estadoProvincia: estadoGeoNorm || m.estadoProvincia,
        pais: paisGeoNorm || m.pais,
        cep: cepGeo?.long_name || m.cep,
      }));
    });
  };

  useEffect(() => {
    if (!googleMapsApiKey) return;
    if ((window as { google?: { maps?: { places?: unknown } } }).google?.maps?.places) {
      setMapsReady(true);
      setMapsError(null);
      return;
    }
    const scriptId = 'google-maps-api-script-naviex';
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => setMapsReady(true), { once: true });
      existing.addEventListener('error', () => setMapsError('Não foi possível carregar o Google Maps.'), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(googleMapsApiKey)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setMapsReady(true);
      setMapsError(null);
    };
    script.onerror = () => setMapsError('Não foi possível carregar o Google Maps.');
    document.head.appendChild(script);
  }, [googleMapsApiKey]);

  useEffect(() => {
    if (!isFormView) {
      autocompleteRef.current = null;
      return;
    }
    const googleMaps = (window as { google?: { maps?: { places?: { Autocomplete: new (input: HTMLInputElement, opts?: Record<string, unknown>) => { addListener: (event: string, cb: () => void) => void; getPlace: () => { geometry?: { location?: { lat: () => number; lng: () => number } }; address_components?: Array<{ long_name: string; short_name: string; types: string[] }> } } } } } }).google?.maps;
    if (!mapsReady || !googleMaps?.places || !cidadeInputRef.current || autocompleteRef.current) return;
    const autocomplete = new googleMaps.places.Autocomplete(cidadeInputRef.current, {
      fields: ['address_components', 'geometry'],
      types: ['(cities)'],
      componentRestrictions: { country: 'br' },
    });
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      applyPlaceToModal(place);
    });
    autocompleteRef.current = autocomplete;
  }, [mapsReady, isFormView]);

  useEffect(() => {
    if (!mapsReady) return;
    const cidadeDigitada = modalCidade.cidade.trim();
    if (cidadeDigitada.length < 3) return;
    const googleMaps = (window as { google?: { maps?: { Geocoder: new () => { geocode: (req: Record<string, unknown>, cb: (results: Array<{ geometry?: { location?: { lat: () => number; lng: () => number } }; address_components?: Array<{ long_name: string; short_name: string; types: string[] }> }> | null, status: string) => void) => void } } } }).google?.maps;
    if (!googleMaps?.Geocoder) return;
    const timer = window.setTimeout(() => {
      const geocoder = new googleMaps.Geocoder();
      geocoder.geocode({ address: cidadeDigitada, componentRestrictions: { country: 'BR' } }, (results, status) => {
        if (status !== 'OK' || !results?.[0]) return;
        applyPlaceToModal(results[0]);
      });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [mapsReady, modalCidade.cidade]);

  useEffect(() => {
    if (!mapsReady || !isValidCoordinates(modalCidade.lat, modalCidade.lng)) return;
    setModalCidadeCoords({ lat: Number(modalCidade.lat), lng: Number(modalCidade.lng) });
  }, [mapsReady, modalCidade.lat, modalCidade.lng]);

  useEffect(() => {
    if (!mapsReady || !modalCidadeCoords || !mapContainerRef.current) return;
    const googleMaps = (window as { google?: { maps?: { Map: new (el: HTMLElement, opts: Record<string, unknown>) => { setCenter: (coords: ParsedCoordinates) => void; setZoom: (zoom: number) => void }; Marker: new (opts: Record<string, unknown>) => { setPosition: (coords: ParsedCoordinates) => void } } } }).google?.maps;
    if (!googleMaps?.Map || !googleMaps?.Marker) return;
    if (!mapRef.current) {
      const map = new googleMaps.Map(mapContainerRef.current, {
        center: modalCidadeCoords,
        zoom: 12,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      const marker = new googleMaps.Marker({ map, position: modalCidadeCoords });
      mapRef.current = map;
      markerRef.current = marker;
      return;
    }
    mapRef.current.setCenter(modalCidadeCoords);
    mapRef.current.setZoom(12);
    markerRef.current?.setPosition(modalCidadeCoords);
  }, [mapsReady, modalCidadeCoords]);

  return (
    <AdminLayout>
      <section className="w-full bg-white min-h-screen max-w-full relative p-6">
        <div className="w-full">
          <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm text-gray-500 mb-1">Home {'>'} Cidades</p>
              <h1 className="text-3xl font-bold text-gray-900">Cidades</h1>
              <p className="text-xs text-gray-500 mt-2">Mesmo cadastro de localização da experiência, agora em página completa.</p>
            </div>
            {!isFormView && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void handleMigrarFotosCidadesParaNuvem()}
                  disabled={migracaoFotosBusy}
                  className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
                >
                  {migracaoFotosBusy ? 'Migrando…' : 'Migrar fotos para S3'}
                </button>
                <button
                  type="button"
                  onClick={openAddCidadeForm}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm font-medium inline-flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Adicionar Cidade
                </button>
              </div>
            )}
          </div>

          {isFormView && (
          <div className="w-full grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="rounded-xl border border-gray-200 p-5 space-y-4">
              {modalError && <p className="text-red-600 text-sm">{modalError}</p>}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cidade / Distrito *</label>
                <input
                  ref={cidadeInputRef}
                  type="text"
                  value={modalCidade.cidade}
                  onChange={(e) => setModalCidade((m) => ({ ...m, cidade: e.target.value }))}
                  placeholder="Cidade ou distrito"
                  className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">País *</label>
                <input
                  type="text"
                  value={modalCidade.pais}
                  readOnly
                  placeholder="Carregado automaticamente"
                  className="w-full py-2 px-3 rounded-lg border border-gray-200 bg-gray-100 text-gray-700 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estado / Província *</label>
                <input
                  type="text"
                  value={modalCidade.estadoProvincia}
                  readOnly
                  placeholder="Carregado automaticamente"
                  className="w-full py-2 px-3 rounded-lg border border-gray-200 bg-gray-100 text-gray-700 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Região(ões) (opcional)</label>
                {modalCidade.estadoProvincia.trim() && regioesDisponiveis.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {regioesDisponiveis.map((r) => (
                      <div key={r} className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg border border-gray-200 bg-gray-50">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={modalCidade.regioes.includes(r)}
                            onChange={() => setModalCidade((m) => ({ ...m, regioes: m.regioes.includes(r) ? m.regioes.filter((x) => x !== r) : [...m.regioes, r] }))}
                            className="rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                          />
                          <span className="text-sm">{r}</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => void deleteRegiao(r)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title={`Excluir região ${r}`}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 flex-wrap items-center">
                  <input
                    type="text"
                    value={novaRegiaoInput}
                    onChange={(e) => setNovaRegiaoInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      e.preventDefault();
                      const r = novaRegiaoInput.trim();
                      if (!r) return;
                      if (modalCidade.estadoProvincia.trim()) {
                        addOrUpdateRegiao(r, modalCidade.estadoProvincia);
                        setRegioesVersion((v) => v + 1);
                      }
                      if (!modalCidade.regioes.includes(r)) setModalCidade((m) => ({ ...m, regioes: [...m.regioes, r] }));
                      setNovaRegiaoInput('');
                    }}
                    placeholder="Nova região (Enter para adicionar)"
                    className="py-1.5 px-2 rounded border border-gray-300 text-sm w-56"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const r = novaRegiaoInput.trim();
                      if (!r) return;
                      if (modalCidade.estadoProvincia.trim()) {
                        addOrUpdateRegiao(r, modalCidade.estadoProvincia);
                        setRegioesVersion((v) => v + 1);
                      }
                      if (!modalCidade.regioes.includes(r)) setModalCidade((m) => ({ ...m, regioes: [...m.regioes, r] }));
                      setNovaRegiaoInput('');
                    }}
                    className="px-2 py-1.5 text-sm text-orange-600 hover:bg-orange-50 rounded"
                  >
                    Adicionar região
                  </button>
                </div>
                {modalCidade.regioes.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {modalCidade.regioes.map((r) => (
                      <div key={`sel-${r}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-orange-200 bg-orange-50">
                        <span className="text-xs text-orange-800">{r}</span>
                        <button
                          type="button"
                          onClick={() => setModalCidade((m) => ({ ...m, regioes: m.regioes.filter((x) => x !== r) }))}
                          className="p-0.5 text-orange-600 hover:text-orange-800"
                          title="Remover da cidade"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => void deleteRegiao(r)}
                          className="p-0.5 text-gray-500 hover:text-red-600"
                          title="Excluir região globalmente"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CEP *</label>
                <input
                  type="text"
                  value={modalCidade.cep}
                  readOnly
                  placeholder="Carregado automaticamente"
                  className="w-full py-2 px-3 rounded-lg border border-gray-200 bg-gray-100 text-gray-700 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fotos da cidade * (até 4, mínimo 1 ao cadastrar)</label>
                <p className="text-xs text-gray-500 mb-2">A primeira foto é a principal. Clique em uma foto para defini-la como principal.</p>
                <p className="text-xs text-gray-500 mb-2">As imagens são otimizadas e enviadas para o armazenamento na nuvem (S3). Aqui fica só a referência — configure S3_IMAGES_BUCKET na API e credenciais AWS.</p>
                <div className="flex flex-wrap gap-3">
                  {(modalCidade.fotos ?? []).map((url, idx) => (
                    <div key={idx} className="relative group">
                      <img src={resolveMediaUrl(url, getApiBase())} alt={`NaviExperience - ${modalCidade.cidade || 'Cidade'} - Foto ${idx + 1}`} title={`NaviExperience - ${modalCidade.cidade || 'Cidade'} - Foto ${idx + 1}`} className="w-20 h-20 object-cover rounded-lg border-2 border-gray-200" />
                      <button
                        type="button"
                        onClick={() => setModalCidade((m) => ({ ...m, fotoPrincipalIndex: idx }))}
                        className={`absolute -top-1 -right-1 text-xs px-1.5 py-0.5 rounded ${(modalCidade.fotoPrincipalIndex ?? 0) === idx ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-600'}`}
                        title="Principal"
                      >
                        {idx + 1}
                      </button>
                      <button
                        type="button"
                        onClick={() => setModalCidade((m) => ({ ...m, fotos: (m.fotos ?? []).filter((_, i) => i !== idx), fotoPrincipalIndex: Math.min(m.fotoPrincipalIndex ?? 0, Math.max(0, (m.fotos ?? []).length - 2)) }))}
                        className="absolute -bottom-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                        title="Remover"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {(modalCidade.fotos ?? []).length < 4 && (
                    <label
                      className={`w-20 h-20 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg transition-colors ${
                        fotoAddBusy ? 'opacity-50 cursor-wait pointer-events-none' : 'cursor-pointer hover:border-orange-400 hover:bg-orange-50/50'
                      }`}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={fotoAddBusy}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (!file) return;
                          void (async () => {
                            setFotoAddBusy(true);
                            try {
                              const dataUrl = await compressImageFileForStorage(file);
                              const blob = await (await fetch(dataUrl)).blob();
                              const jpegFile = new File([blob], 'cidade.jpg', { type: blob.type || 'image/jpeg' });
                              const { reference } = await uploadAdminMedia(jpegFile, 'cidades');
                              setModalCidade((m) => ({ ...m, fotos: [...(m.fotos ?? []), reference].slice(0, 4) }));
                            } catch (err) {
                              await showAlert(
                                err instanceof Error ? err.message : 'Não foi possível enviar a imagem.',
                                'Erro ao adicionar foto'
                              );
                            } finally {
                              setFotoAddBusy(false);
                            }
                          })();
                        }}
                      />
                      <span className="text-2xl text-gray-400">{fotoAddBusy ? '…' : '+'}</span>
                      <span className="text-xs text-gray-500">{fotoAddBusy ? 'Otimizando' : 'Adicionar'}</span>
                    </label>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/50">
                <span className="text-sm font-medium text-gray-700 block mb-2">Transporte (translado partindo desta cidade)</span>
                <p className="text-xs text-gray-500 mb-2">Marque as opções de veículo disponíveis para translado a partir desta cidade.</p>
                <div className="flex flex-wrap gap-3">
                  {[
                    { key: 'passeio' as const, label: 'Passeio' },
                    { key: 'suv4x4' as const, label: 'SUV (5 lugares)' },
                    { key: 'suv7lugares' as const, label: 'SUV (7 lugares)' },
                    { key: 'taxi' as const, label: 'Táxi' },
                    { key: 'van16' as const, label: 'Van 16' },
                    { key: 'van22' as const, label: 'Van 22' },
                  ].map(({ key, label }) => {
                    const opts = modalCidade.transportOpcoes ?? {};
                    return (
                      <label key={key} className="inline-flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!opts[key]}
                          onChange={(e) => {
                            const next = { ...opts, [key]: e.target.checked };
                            const hasAny = Object.values(next).some(Boolean);
                            setModalCidade((m) => ({ ...m, transportOpcoes: hasAny ? next : undefined }));
                          }}
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-2">
                <button type="button" onClick={goToMainPage} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">Cancelar</button>
                <button
                  type="button"
                  onClick={() => void saveCidade()}
                  disabled={savingCidade}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingCidade ? 'Salvando…' : modalCidade.id ? 'Atualizar' : 'Cadastrar'}
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 p-5 w-full">
              <p className="text-sm font-medium text-gray-700 mb-2">Localização no mapa</p>
              {!googleMapsApiKey && (
                <p className="mb-2 text-xs text-amber-700">Configure `VITE_GOOGLE_MAPS_API_KEY` para habilitar autocomplete e mapa.</p>
              )}
              {mapsError && (
                <p className="mb-2 text-xs text-red-600">{mapsError}</p>
              )}
              <div className="rounded-lg border border-gray-200 bg-gray-50 h-full min-h-[520px] overflow-hidden">
                {mapsReady && modalCidadeCoords ? (
                  <div ref={mapContainerRef} className="w-full h-full" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm px-3 text-center">
                    Digite a cidade e selecione uma sugestão para preencher os campos e visualizar o mapa.
                  </div>
                )}
              </div>
            </div>
          </div>
          )}

          {!isFormView && (
          <div className="mt-6 rounded-xl border border-gray-200 p-5 w-full">
            <p className="text-base font-semibold text-gray-800 mb-3">Cidades cadastradas</p>
            <ul className="space-y-3 max-h-80 overflow-auto">
              {listCidades.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-4 py-3.5 px-4 rounded-lg bg-gray-50 border border-gray-100">
                  <span className="text-base sm:text-lg text-gray-900 font-medium truncate min-w-0">
                    {c.cidade}
                    {c.estadoProvincia && `, ${c.estadoProvincia}`}
                    {c.cep && ` — ${c.cep}`}
                  </span>
                  <div className="flex gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => editCidade(c)}
                      className="p-2.5 rounded-lg text-gray-600 hover:text-orange-600 hover:bg-orange-50"
                      title="Editar"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5M16.5 3.5a2.121 2.121 0 113 3L12 14l-4 1 1-4 7.5-7.5z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => void deleteCidade(c)}
                      className="p-2.5 rounded-lg text-gray-600 hover:text-red-600 hover:bg-red-50"
                      title="Excluir"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          )}
        </div>
      </section>
    </AdminLayout>
  );
}

